// Siparişler sayfası (Cari / E-Ticaret / B2B sekmeleri) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'documents.ordersPage';

export const ordersPageGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Siparişler sayfası nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Carilerinize verdiğiniz ve onlardan aldığınız siparişleri, web sitenizden gelen siparişleri ve B2B bağlantılarınızı tek sayfadan izlersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  navItems: [
    t(`${G}.nav.cari`, 'Cari Siparişler'),
    t(`${G}.nav.eticaret`, 'E-Ticaret Siparişleri'),
    t(`${G}.nav.b2b`, 'B2B Bağlantıları'),
  ],
  regionLabels: {
    header: t(`${G}.regions.header`, 'Özet kartları ve düğmeler'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Sipariş listesi'),
    footer: t(`${G}.regions.footer`, 'Sayfalama'),
  },
  steps: [
    {
      region: 'tabs',
      title: t(`${G}.s1.title`, '1. Doğru sekmeyi seçin'),
      text: t(`${G}.s1.text`, 'Cari Siparişler: carilerinizle yaptığınız satış ve alış siparişleri. E-Ticaret Siparişleri: kendi web sitenizden gelen siparişler (pazaryeri siparişleri bu sekmede görünmez). B2B Bağlantıları: Aquasoft kullanan firmalarla kurduğunuz bağlantılar ve onlardan gelen alış siparişleri; bu sekmenin ayrıntıları kendi (i) rehberindedir.'),
    },
    {
      region: 'header',
      title: t(`${G}.s2.title`, '2. Özet kartlarını okuyun'),
      text: t(`${G}.s2.text`, 'Cari Siparişler sekmesindeki üst kartlar o an listelenen siparişlere göre hesaplanır.'),
      tips: [
        t(`${G}.s2.tip1`, 'Bekleyen: Taslak ve Beklemede durumundaki siparişlerin sayısı ve tutarı.'),
        t(`${G}.s2.tip2`, 'Onaylı / Sevkiyatta: Onaylandı ve Sevk Edildi durumundaki siparişler.'),
        t(`${G}.s2.tip3`, 'Teslimat Süresi Geçmiş: teslimat tarihi geçmiş siparişler; listede de uyarı simgesiyle işaretlenir.'),
        t(`${G}.s2.tip4`, 'Siparişten Faturaya Dönüş Oranı: taslaklar hariç siparişlerin yüzde kaçının faturalandırıldığı.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s3.title`, '3. Yeni sipariş girin'),
      text: t(`${G}.s3.text`, '"Satış Siparişi" veya "Alış Siparişi" düğmesi sipariş formunu açar. Yeni siparişi kaydederken stok sorusu gelir: satışta "stoktan düşülsün mü?", alışta "stoğa kaydedilsin mi?". Evet derseniz ürünler hemen stoktan düşülür / stoğa eklenir ve bu geri alınamaz.'),
      tips: [
        t(`${G}.s3.tip1`, 'Listedeki Stok sütunu bu seçimi gösterir: Evet (yeşil) stok işlendi, Hayır (kırmızı) işlenmedi.'),
        t(`${G}.s3.tip2`, 'Var olan bir siparişi düzenleyip kaydettiğinizde stok sorusu sorulmaz.'),
        t(`${G}.s3.tip3`, '⋮ düğmesindeki "Excel İndir" satış ve alış siparişlerini ayrı sayfalarda Excel dosyasına aktarır.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Siparişi bulun'),
      text: t(`${G}.s4.text`, 'Arama kutusuna aradığınız siparişi yazın (ör. sipariş no); Sipariş Türü ile Satış, Alış ya da B2B siparişlerini ayırın. Filtre simgesi Durum filtresini ve tarih sıralamasını (Yeniden Eskiye / Eskiden Yeniye) açar.'),
      tips: [
        t(`${G}.s4.tip1`, 'Durumlar: Taslak, Beklemede, Onaylandı, Sevk Edildi, Teslim Edildi, İptal Edildi, Faturalandırıldı.'),
        t(`${G}.s4.tip2`, '"Tümü" seçiliyken B2B siparişleri ilk sayfada diğer siparişlerle tarih sırasına göre karışık gösterilir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Faturaya veya irsaliyeye dönüştürün'),
      text: t(`${G}.s5.text`, 'Satırın ⋮ menüsünde Görüntüle, Düzenle ve Sil bulunur. Sipariş Onaylandı veya Teslim Edildi durumundaysa "Fatura Oluştur", Onaylandı veya Sevk Edildi durumundaysa "İrsaliye Oluştur" görünür; fatura / irsaliye formu sipariş bilgileriyle dolu açılır, kaydetmeden önce kontrol edin.'),
      tips: [
        t(`${G}.s5.tip1`, 'Fatura veya irsaliye zaten oluşturulduysa menüde "Fatura Göster" / "İrsaliye Göster" çıkar.'),
        t(`${G}.s5.tip2`, 'Durum etiketinin üzerine gelince bağlı teklif, irsaliye ve fatura numaraları görünür.'),
        t(`${G}.s5.tip3`, 'Silme işlemi geri alınamaz.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. B2B siparişlerini işleyin'),
      text: t(`${G}.s6.text`, 'Bağlı firmalardan gelen B2B siparişleri listede mavi "B2B" etiketiyle görünür. Satırdaki göz simgesi ("Detay / Faturalaştır") sipariş detayını açar; stok bu siparişlerde faturalaştırma sırasında işlenir, bu yüzden Stok sütununda "—" görünür.'),
    },
    {
      region: 'footer',
      title: t(`${G}.s7.title`, '7. Sayfalar arasında gezin'),
      text: t(`${G}.s7.text`, 'Liste uzunsa alttaki sayfalama ile ilerleyin ve sayfa başına satır sayısını seçin. Sekme çubuğunun sağındaki (i) bu rehberi yeniden açar.'),
    },
  ],
});
